import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ListChecks, Plus, Trash2, Flame, ChevronDown, Filter, Loader2, Clock,
  CheckCircle2, Circle, X, AlertCircle, CalendarDays, Sparkles, Mic, MicOff, Send, Bot,
  Phone, RefreshCw, AlertTriangle, Cake, PartyPopper, Trophy,
} from "lucide-react";
import { endpoints } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";

// Priority badges use the semantic palette: high=risk(red), medium=leads(amber),
// low=ops(slate). Keeps the visual language consistent with the sidebar / cards.
const PRIORITY_META = {
  high:   { label: "High",   cls: "bg-risk/10 text-risk",   dot: "bg-risk" },
  medium: { label: "Medium", cls: "bg-leads/10 text-leads", dot: "bg-leads" },
  low:    { label: "Low",    cls: "bg-ops/10 text-ops",     dot: "bg-ops" },
};

// Task-type → icon + tone (which area of the gym this task belongs to)
function taskTypeMeta(t) {
  const kind = t.auto_kind || t.type || "";
  if (kind.includes("birthday")) return { Icon: Cake, tone: "ai", label: "Birthday" };
  if (kind.includes("anniversary")) return { Icon: PartyPopper, tone: "ai", label: "Anniversary" };
  if (kind.includes("milestone")) return { Icon: Trophy, tone: "ai", label: "Milestone" };
  if (kind.includes("renewal")) return { Icon: RefreshCw, tone: "growth", label: "Renewal" };
  if (kind.includes("trial")) return { Icon: CalendarDays, tone: "trials", label: "Trial" };
  if (kind.includes("follow_up")) return { Icon: Phone, tone: "leads", label: "Lead" };
  if (kind.includes("at_risk") || kind.includes("inactivity")) return { Icon: AlertTriangle, tone: "risk", label: "At-risk" };
  if (t.linked_lead_id) return { Icon: Phone, tone: "leads", label: "Lead" };
  if (t.linked_member_id) return { Icon: RefreshCw, tone: "growth", label: "Member" };
  return { Icon: ListChecks, tone: "ops", label: "Task" };
}

const TONE_ICON = {
  growth: "text-growth bg-growth/10",
  leads:  "text-leads  bg-leads/10",
  trials: "text-trials bg-trials/10",
  tasks:  "text-tasks  bg-tasks/10",
  ai:     "text-ai     bg-ai/10",
  risk:   "text-risk   bg-risk/10",
  ops:    "text-muted-foreground bg-muted",
};

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function Tasks() {
  const { isDemo } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");          // all | today | overdue | done
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // Idempotently generate today's auto-tasks first - but skip in demo
      // mode (POST would trigger LoginRequiredModal).
      if (!isDemo) {
        try { await endpoints.syncTodayTasks(); } catch (_) {}
      }
      const [tRes, sRes] = await Promise.all([endpoints.listTasks(), endpoints.listGymStaff()]);
      setTasks(tRes.data || []);
      setStaff(sRes.data || []);
    } catch {
      toast.error("Could not load tasks");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [isDemo]); // eslint-disable-line react-hooks/exhaustive-deps

  const today = todayKey();
  const groups = useMemo(() => {
    const todayList = [];
    const overdueList = [];
    const upcomingList = [];
    const doneList = [];
    for (const t of tasks) {
      if (t.status === "done") { doneList.push(t); continue; }
      if (t.due_date) {
        if (t.due_date < today) overdueList.push(t);
        else if (t.due_date === today) todayList.push(t);
        else upcomingList.push(t);
      } else {
        todayList.push(t);
      }
    }
    const sortByPri = (arr) => {
      const order = { high: 0, medium: 1, low: 2 };
      return arr.sort((a, b) => (order[a.priority] ?? 1) - (order[b.priority] ?? 1));
    };
    return { today: sortByPri(todayList), overdue: sortByPri(overdueList), upcoming: sortByPri(upcomingList), done: doneList };
  }, [tasks, today]);

  const filteredVisible = useMemo(() => {
    let groupsToShow = { today: [], overdue: [], upcoming: [], done: [] };
    if (filter === "all") groupsToShow = { ...groups, done: [] };
    else if (filter === "today") groupsToShow = { today: groups.today, overdue: [], upcoming: [], done: [] };
    else if (filter === "overdue") groupsToShow = { today: [], overdue: groups.overdue, upcoming: [], done: [] };
    else if (filter === "done") groupsToShow = { today: [], overdue: [], upcoming: [], done: groups.done };
    if (priorityFilter !== "all") {
      Object.keys(groupsToShow).forEach(k => {
        groupsToShow[k] = groupsToShow[k].filter(t => t.priority === priorityFilter);
      });
    }
    return groupsToShow;
  }, [groups, filter, priorityFilter]);

  const counts = {
    today: groups.today.length,
    overdue: groups.overdue.length,
    upcoming: groups.upcoming.length,
    done: groups.done.length,
    total: tasks.length,
  };

  const totalVisible = filteredVisible.today.length + filteredVisible.overdue.length + filteredVisible.upcoming.length + filteredVisible.done.length;

  const onToggle = async (task) => {
    const next = task.status === "done" ? "pending" : "done";
    setTasks(curr => curr.map(t => t.id === task.id ? { ...t, status: next } : t));
    try {
      await endpoints.updateTask(task.id, { status: next });
    } catch {
      toast.error("Could not update task");
      load();
    }
  };

  const onDelete = async (task) => {
    if (!window.confirm(`Delete "${task.title}"?`)) return;
    setTasks(curr => curr.filter(t => t.id !== task.id));
    try {
      await endpoints.deleteTask(task.id);
      toast.success("Task deleted");
    } catch {
      toast.error("Could not delete");
      load();
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-6xl mx-auto" data-testid="tasks-page">
      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-tasks mb-2">Action board</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-foreground flex items-center gap-2.5" data-testid="tasks-title">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-tasks/10 text-tasks">
              <ListChecks className="w-5 h-5" strokeWidth={2.4} />
            </span>
            Tasks
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5">Daily to-dos · what gets done today shows up here.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            data-testid="task-add-btn"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors shadow-[0_8px_24px_-8px_rgba(37,99,235,0.5)]"
          >
            <Plus className="w-4 h-4" /> New task
          </button>
        </div>
      </header>

      {/* AI quick-create bar */}
      <AITaskBar
        onCreated={(t) => setTasks(curr => [t, ...curr])}
      />

      {/* Filter pills */}
      <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="task-filters">
        <FilterPill k="all" current={filter} onClick={setFilter} count={counts.total} label="All open" />
        <FilterPill k="today" current={filter} onClick={setFilter} count={counts.today} label="Today" blink />
        <FilterPill k="overdue" current={filter} onClick={setFilter} count={counts.overdue} label="Overdue" tone="rose" blink />
        <FilterPill k="done" current={filter} onClick={setFilter} count={counts.done} label="Done" tone="emerald" />
        <span className="mx-1 text-muted-foreground/30">|</span>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-[150px]" data-testid="priority-filter"><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground" data-testid="tasks-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : totalVisible === 0 ? (
        <EmptyState onAdd={() => setCreateOpen(true)} filter={filter} priority={priorityFilter} />
      ) : (
        <div className="space-y-8">
          {filteredVisible.overdue.length > 0 && (
            <Section
              title="Overdue"
              icon={<AlertCircle className="w-4 h-4 text-risk" />}
              count={filteredVisible.overdue.length}
              testid="section-overdue"
            >
              {filteredVisible.overdue.map(t => <TaskRow key={t.id} task={t} staff={staff} onToggle={onToggle} onDelete={onDelete} onChange={load} overdue />)}
            </Section>
          )}
          {filteredVisible.today.length > 0 && (
            <Section
              title="Today"
              icon={<Flame className="w-4 h-4 text-trials" />}
              count={filteredVisible.today.length}
              testid="section-today"
            >
              {filteredVisible.today.map(t => <TaskRow key={t.id} task={t} staff={staff} onToggle={onToggle} onDelete={onDelete} onChange={load} />)}
            </Section>
          )}
          {filteredVisible.upcoming.length > 0 && (
            <Section
              title="Upcoming"
              icon={<CalendarDays className="w-4 h-4 text-muted-foreground" />}
              count={filteredVisible.upcoming.length}
              testid="section-upcoming"
            >
              {filteredVisible.upcoming.map(t => <TaskRow key={t.id} task={t} staff={staff} onToggle={onToggle} onDelete={onDelete} onChange={load} />)}
            </Section>
          )}
          {filteredVisible.done.length > 0 && (
            <Section
              title="Completed"
              icon={<CheckCircle2 className="w-4 h-4 text-growth" />}
              count={filteredVisible.done.length}
              testid="section-done"
              muted
            >
              {filteredVisible.done.map(t => <TaskRow key={t.id} task={t} staff={staff} onToggle={onToggle} onDelete={onDelete} onChange={load} />)}
            </Section>
          )}
        </div>
      )}

      <CreateTaskDialog open={createOpen} onOpenChange={setCreateOpen} staff={staff} onCreated={(nt) => { setTasks(curr => [nt, ...curr]); }} />
    </div>
  );
}

function FilterPill({ k, current, onClick, count, label, tone, blink }) {
  const active = current === k;
  // All active states use blue family per design choice; non-active uses neutral.
  const toneActive = {
    blue: "bg-blue-600 text-white shadow-sm",
    rose: "bg-rose-500 text-white",
    emerald: "bg-emerald-500 text-white",
  }[tone] || "bg-blue-600 text-white shadow-sm";
  return (
    <button
      type="button"
      data-testid={`filter-${k}`}
      onClick={() => onClick(k)}
      className={`relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
        active
          ? toneActive
          : "bg-card border border-border text-muted-foreground hover:text-foreground hover:border-blue-300"
      }`}
    >
      {blink && count > 0 && (
        <span className="relative flex h-2 w-2 mr-0.5" aria-hidden>
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500" />
        </span>
      )}
      {label}
      <span className={`text-[10px] font-bold px-1.5 rounded-full ${
        active ? "bg-white/25" : "bg-muted text-muted-foreground/80"
      }`}>{count}</span>
    </button>
  );
}

function Section({ title, icon, count, children, testid, muted }) {
  return (
    <section data-testid={testid} className={muted ? "opacity-80" : ""}>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-foreground/80">{title}</h2>
        <span className="text-xs text-muted-foreground/70">{count}</span>
      </div>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function TaskRow({ task, staff, onToggle, onDelete, onChange, overdue }) {
  const pri = PRIORITY_META[task.priority] || PRIORITY_META.medium;
  const tmeta = taskTypeMeta(task);
  const done = task.status === "done";
  const assignee = task.assignee_user_id ? staff.find(s => s.user_id === task.assignee_user_id) : null;
  const due = task.due_date ? new Date(task.due_date + "T00:00") : null;
  const linkedHref = task.linked_member_id
    ? `/members/${task.linked_member_id}?wa=task`
    : task.linked_lead_id
      ? `/leads/${task.linked_lead_id}?wa=task`
      : null;

  return (
    <div
      data-testid={`task-row-${task.id}`}
      className={`group flex items-start gap-3 p-3.5 rounded-xl border transition-all ${
        done ? "bg-muted/30 border-border/50" : "bg-card border-border hover:border-foreground/20 hover:shadow-sm"
      } ${overdue && !done ? "border-risk/40" : ""}`}
    >
      <button
        type="button"
        onClick={() => onToggle(task)}
        data-testid={`task-toggle-${task.id}`}
        className={`mt-0.5 shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
          done
            ? "bg-growth border-growth"
            : "border-border hover:border-growth"
        }`}
        aria-label={done ? "Mark pending" : "Mark done"}
      >
        {done && <CheckCircle2 className="w-3 h-3 text-white" />}
      </button>
      <span className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${TONE_ICON[tmeta.tone]}`} title={tmeta.label}>
        <tmeta.Icon className="w-4 h-4" strokeWidth={2.4} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className={`text-sm font-medium ${done ? "line-through text-muted-foreground" : "text-foreground"}`}>
              {task.title}
            </p>
            {task.notes && (
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{task.notes}</p>
            )}
            <div className="flex items-center gap-2 flex-wrap mt-1.5 text-[11px]">
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium ${pri.cls}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${pri.dot}`} />
                {pri.label}
              </span>
              <span className="text-muted-foreground/70 font-medium">{tmeta.label}</span>
              {due && (
                <span className={`inline-flex items-center gap-1 ${overdue && !done ? "text-risk font-semibold" : "text-muted-foreground"}`}>
                  <Clock className="w-3 h-3" />
                  {due.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </span>
              )}
              {assignee && (
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <span className="w-3.5 h-3.5 rounded-full bg-primary/15 text-primary text-[8px] font-bold flex items-center justify-center">
                    {(assignee.name || "?")[0]}
                  </span>
                  {assignee.name?.split(" ")[0]}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {linkedHref && (
          <Link
            to={linkedHref}
            data-testid={`task-manage-${task.id}`}
            className={`opacity-80 group-hover:opacity-100 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold ${TONE_ICON[tmeta.tone]} hover:opacity-100 transition-opacity`}
            title="Open & WhatsApp"
          >
            Manage
          </Link>
        )}
        <button
          type="button"
          onClick={() => onDelete(task)}
          data-testid={`task-delete-${task.id}`}
          className="opacity-0 group-hover:opacity-100 p-1.5 text-muted-foreground/70 hover:text-risk hover:bg-risk/10 rounded-md transition-all"
          aria-label="Delete task"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function EmptyState({ onAdd, filter, priority }) {
  let copy = "You're all caught up. Add a task to plan ahead.";
  let title = "No tasks yet";
  if (filter === "today") { title = "Quiet day"; copy = "Nothing scheduled for today. Add a task or enjoy the calm."; }
  else if (filter === "overdue") { title = "Nothing slipped"; copy = "No overdue tasks. Strong execution."; }
  else if (filter === "done") { title = "Nothing finished yet"; copy = "Completed tasks will appear here as you check them off."; }
  if (priority !== "all") { title = "Nothing here"; copy = `No ${priority}-priority tasks in this view.`; }
  return (
    <div className="border border-dashed border-border rounded-2xl py-14 text-center" data-testid="tasks-empty">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-tasks/10 text-tasks mb-3">
        <ListChecks className="w-5 h-5" strokeWidth={2.2} />
      </div>
      <p className="font-display text-base font-semibold text-foreground mb-1">{title}</p>
      <p className="text-sm text-muted-foreground mb-4 max-w-sm mx-auto leading-relaxed">{copy}</p>
      <button
        onClick={onAdd}
        data-testid="empty-add-btn"
        className="inline-flex items-center gap-2 bg-tasks hover:bg-tasks/90 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
      >
        <Plus className="w-4 h-4" /> Add a task
      </button>
    </div>
  );
}

function CreateTaskDialog({ open, onOpenChange, staff, onCreated }) {
  const [form, setForm] = useState({
    title: "", notes: "", priority: "medium", due_date: "", assignee_user_id: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm({ title: "", notes: "", priority: "medium", due_date: todayKey(), assignee_user_id: "" });
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setSubmitting(true);
    try {
      const { data } = await endpoints.createTask({
        title: form.title.trim(),
        notes: form.notes,
        priority: form.priority,
        due_date: form.due_date || null,
        assignee_user_id: form.assignee_user_id || null,
      });
      onCreated && onCreated(data);
      toast.success("Task added");
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create task");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">New task</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="task-form">
          <div>
            <Label>Task</Label>
            <Input
              data-testid="task-title-input"
              autoFocus
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Call Rohit about renewal"
            />
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Textarea
              data-testid="task-notes-input"
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              rows={2}
              placeholder="Any context that helps the owner of this task"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Due date</Label>
              <Input
                type="date"
                data-testid="task-due-input"
                value={form.due_date}
                onChange={e => setForm({ ...form, due_date: e.target.value })}
              />
            </div>
          </div>
          {staff.length > 1 && (
            <div>
              <Label>Assign to (optional)</Label>
              <Select value={form.assignee_user_id || "__none"} onValueChange={v => setForm({ ...form, assignee_user_id: v === "__none" ? "" : v })}>
                <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Unassigned</SelectItem>
                  {staff.map(s => (
                    <SelectItem key={s.user_id} value={s.user_id}>{s.name} {s.role === "owner" ? "· owner" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter>
            <button
              type="submit"
              disabled={submitting}
              data-testid="task-submit-btn"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add task
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AITaskBar({ onCreated }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const finalRef = useRef("");          // accumulated final transcript during hold
  const baseTextRef = useRef("");       // text in box BEFORE hold started
  const autoSubmitRef = useRef(false);  // whether to submit on release
  const supportsSpeech = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

  const submit = async (descArg) => {
    const desc = (descArg ?? text).trim();
    if (desc.length < 3) {
      toast.error("Describe the task in a few words");
      return;
    }
    setLoading(true);
    try {
      const { data } = await endpoints.aiCreateTask(desc);
      onCreated && onCreated(data);
      setText("");
      toast.success("Task added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI couldn't parse - try rephrasing");
    } finally { setLoading(false); }
  };

  const startListening = () => {
    if (!supportsSpeech) {
      toast.error("Voice input isn't supported in this browser. Try Chrome or Safari.");
      return;
    }
    if (recognitionRef.current) return; // already running
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SpeechRecognition();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-IN";
    finalRef.current = "";
    baseTextRef.current = text;
    rec.onstart = () => setListening(true);
    rec.onerror = (e) => {
      const m = e?.error || "error";
      if (m === "not-allowed" || m === "service-not-allowed") {
        toast.error("Microphone access blocked. Allow it in your browser, then try again.");
      } else if (m === "no-speech") {
        // silent - common
      } else if (m !== "aborted") {
        toast.error("Voice input failed: " + m);
      }
      setListening(false);
    };
    rec.onend = () => {
      setListening(false);
      recognitionRef.current = null;
      const combined = (baseTextRef.current + " " + finalRef.current).trim().replace(/\s+/g, " ");
      if (autoSubmitRef.current && combined.length >= 3) {
        autoSubmitRef.current = false;
        // small delay so the textarea state flushes visually
        setTimeout(() => submit(combined), 60);
      }
    };
    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) finalRef.current += r[0].transcript + " ";
        else interim += r[0].transcript;
      }
      const combined = (baseTextRef.current + " " + finalRef.current + interim).trim().replace(/\s+/g, " ");
      setText(combined);
    };
    recognitionRef.current = rec;
    try { rec.start(); } catch (_) { /* already started */ }
  };

  const stopListening = (autoSubmit = false) => {
    autoSubmitRef.current = !!autoSubmit;
    const rec = recognitionRef.current;
    if (rec) {
      try { rec.stop(); } catch (_) {}
    } else {
      setListening(false);
    }
  };

  // Press & hold (pointer down/up) - works mouse + touch via Pointer Events
  const onMicDown = (e) => {
    e.preventDefault();
    startListening();
  };
  const onMicUp = (e) => {
    if (!listening) return;
    e.preventDefault();
    stopListening(true);
  };
  const onMicLeave = () => {
    if (listening) stopListening(true);
  };

  return (
    <div
      data-testid="ai-task-bar"
      className="mb-6 relative overflow-hidden rounded-2xl border border-blue-200/60 dark:border-blue-900/40 bg-gradient-to-br from-blue-50/70 via-sky-50/40 to-indigo-50/40 dark:from-blue-950/30 dark:via-sky-950/20 dark:to-indigo-950/20"
    >
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-blue-400/15 blur-3xl pointer-events-none" />
      <div className="relative p-4 md:p-5">
        <div className="flex items-center gap-2 mb-2.5">
          <span
            className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-white shrink-0"
            style={{ background: "linear-gradient(135deg, #3B82F6 0%, #6366F1 60%, #8B5CF6 100%)" }}
            aria-hidden
          >
            <Sparkles className="w-3.5 h-3.5" />
          </span>
          <p className="text-sm font-semibold text-foreground">Quick add with AI</p>
          <span className="text-[10px] font-bold uppercase tracking-wider bg-blue-600 text-white px-1.5 py-0.5 rounded">Beta</span>
        </div>
        <div className="relative">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !loading) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder='Type - or hold the mic to speak. e.g. "Follow up with Aman tomorrow about his trial, high priority"'
            data-testid="ai-task-input"
            rows={2}
            className="w-full resize-none bg-card/70 border border-border/80 rounded-lg pl-3 pr-[6.5rem] py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:border-blue-400/60"
          />
          {/* Mic and Add actions sit absolutely inside the textarea row */}
          <div className="absolute right-2 bottom-2 flex items-center gap-1.5">
            {supportsSpeech && (
              <button
                type="button"
                onPointerDown={onMicDown}
                onPointerUp={onMicUp}
                onPointerLeave={onMicLeave}
                onPointerCancel={onMicLeave}
                onContextMenu={(e) => e.preventDefault()}
                data-testid="ai-task-mic"
                aria-label={listening ? "Recording - release to add" : "Press and hold to record"}
                title={listening ? "Release to add task" : "Press and hold to speak"}
                className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors select-none touch-none ${
                  listening
                    ? "bg-rose-500 text-white shadow-lg ring-4 ring-rose-500/25"
                    : "bg-blue-500 hover:bg-blue-600 text-white"
                }`}
              >
                {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
            )}
            <button
              type="button"
              onClick={() => submit()}
              disabled={loading || text.trim().length < 3}
              data-testid="ai-task-submit"
              className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-xs font-semibold h-9 px-3 rounded-lg transition-colors"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Add
            </button>
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
          <p className="text-[11px] text-muted-foreground/80">
            AI fills priority, due date, notes - and links the task to a member if it can match a name.
          </p>
          {listening && (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-rose-600" data-testid="ai-task-listening-state">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500" />
              </span>
              Recording - release to add
            </span>
          )}
          {!supportsSpeech && (
            <span className="text-[11px] text-muted-foreground/70">(Voice needs Chrome or Safari.)</span>
          )}
        </div>
      </div>
    </div>
  );
}

